import { memo, useState } from "react";
import { Chip, Tooltip as HeroTooltip } from "@heroui/react";
import { PencilIcon } from "@heroicons/react/24/solid";
import type {
  ColDef,
  ControlEntry,
  EditTarget,
  EntityType,
  Row,
} from "../domain/types";
import {
  buildPreviousValueDeltaIndicator,
  getDotClassesFromColorCode,
  getDotStatus,
  getCanonicalFieldKey,
  makeOverrideKey,
  prepareValueForFormatter,
} from "../domain/status";

export const ReconciliationCell = memo(function ReconciliationCell({
  entityType,
  entityId,
  fundId,
  issuerId,
  reportingDate,
  row,
  col,
  fundName,
  companyName,
  controls,
  controlCellKeyByTuple,
  previousControls,
  previousAuditValues,
  previousRow,
  disablePreviousComparison = false,
  hideDot = false,
  onEdit,
  canEdit = true,
  editLockTooltip,
}: {
  entityType: EntityType;
  entityId: number;
  fundId: number;
  issuerId?: number | null;
  reportingDate?: number | null;
  row: Row;
  col: ColDef;
  fundName: string;
  companyName: string;
  controls: Record<string, ControlEntry>;
  controlCellKeyByTuple: Record<string, string>;
  previousControls: Record<string, ControlEntry | undefined>;
  previousAuditValues: Record<string, string>;
  previousRow?: Row;
  disablePreviousComparison?: boolean;
  hideDot?: boolean;
  onEdit: (target: EditTarget) => void;
  canEdit?: boolean;
  editLockTooltip?: string;
}) {
  const [hasBeenHovered, setHasBeenHovered] = useState(false);
  const normalizedColKey = getCanonicalFieldKey(col.key);
  const positionScopeId = entityType === "position" ? entityId : null;
  const isMissingPositionId = entityType === "position" && !positionScopeId;
  const tupleKey =
    entityType === "fund"
      ? `fund:${fundId}:0:0:${normalizedColKey}`
      : entityType === "position"
        ? `position:${fundId}:${positionScopeId ?? 0}:${reportingDate ?? 0}:${normalizedColKey}`
        : `issuer:${fundId}:${issuerId ?? entityId}:${reportingDate ?? 0}:${normalizedColKey}`;
  // Backend is the source of truth for reconciliation cell_key.
  // For issuer/position, tupleKey already follows the backend cell_key contract.
  const directControlKey = entityType === "fund" ? "" : tupleKey;
  const canonicalControlKey =
    (directControlKey && controls[directControlKey] ? directControlKey : "") ||
    controlCellKeyByTuple[tupleKey];
  const fallbackControlKey =
    entityType === "fund"
      ? `fund:${fundId}:0:${normalizedColKey}`
      : entityType === "position"
        ? ""
        : `issuer:${fundId}:${issuerId ?? entityId}:${reportingDate ?? 0}:${normalizedColKey}`;
  const controlKey = canonicalControlKey || fallbackControlKey;
  const control = controls[controlKey];
  const rawSource = { trr: control?.trrValue, pdf: control?.pdfValue };
  const baseValue = row[col.key];
  const baseDisplay = col.fmt(baseValue);
  const isNumericColumn = col.align === "right";
  const goldenDisplay = (() => {
    // For numeric columns, apply local formatter to preserve precision display.
    // For all other columns, the backend pre-formats display_value (bool → Yes/No, tags, old_names, etc.)
    if (isNumericColumn) {
      const v = control?.goldenValue;
      if (v === null || v === undefined || String(v).trim() === "") return "";
      return col.fmt(prepareValueForFormatter(v, true));
    }
    const dv = control?.displayValue;
    if (dv !== null && dv !== undefined && String(dv).trim() !== "")
      return col.isArray ? col.fmt(dv) : dv;
    const v = control?.goldenValue;
    if (v === null || v === undefined || String(v).trim() === "") return "";
    return col.fmt(prepareValueForFormatter(v, false));
  })();
  const displayValue = goldenDisplay || baseDisplay;
  const status = getDotStatus(
    control?.hasOverride ?? false,
    displayValue,
    control,
    control?.status,
  );
  const dotClasses = getDotClassesFromColorCode(control?.colorCode, status);
  // Intentionally preserved behavior: previous comparison can be disabled per-cell.
  const previousControl = disablePreviousComparison
    ? undefined
    : previousControls[controlKey];
  const previousAuditValue = disablePreviousComparison
    ? undefined
    : previousAuditValues[makeOverrideKey(entityType, entityId, col.key)];
  const previousValue = disablePreviousComparison
    ? undefined
    : control?.previousValue ||
      previousControl?.goldenValue ||
      previousControl?.trrValue ||
      previousControl?.pdfValue ||
      previousAuditValue ||
      (isNumericColumn &&
      previousRow?.[col.key] != null &&
      previousRow[col.key] !== ""
        ? String(previousRow[col.key])
        : undefined);
  const currentForDelta = String(
    control?.goldenValue ?? baseValue ?? "",
  ).trim();
  const shownDelta =
    isNumericColumn && currentForDelta && previousValue
      ? buildPreviousValueDeltaIndicator(currentForDelta, String(previousValue))
      : null;

  const formatTooltipValue = (value: unknown): string => {
    if (value === null || value === undefined || value === "") return "—";
    return col.fmt(prepareValueForFormatter(value, isNumericColumn));
  };
  const shownTrr = rawSource?.trr;
  const shownPdf = rawSource?.pdf;

  const startEdit = () =>
    !isMissingPositionId &&
    onEdit({
      entityType,
      entityId,
      fundId,
      reportingDate,
      fieldKey: col.key,
      fieldLabel: col.header,
      companyName,
      rawValue: baseValue,
      formattedValue: baseDisplay,
      goldenValue: control?.goldenValue,
      trrValue: rawSource.trr,
      pdfValue: rawSource.pdf,
      control,
      fundName,
      isArray: col.isArray ?? false,
    });

  const tooltipContent = hasBeenHovered ? (
    <div className="min-w-[220px] max-w-[360px] space-y-2 text-xs">
      <p className="border-b border-slate-200 pb-1 font-semibold text-slate-800">
        Source values
      </p>
      <div className="grid grid-cols-[56px_1fr] items-start gap-x-2 gap-y-1">
        <Chip size="sm" color="warning" variant="flat" className="h-5 min-w-0">
          Golden
        </Chip>
        <span className="pt-0.5 text-slate-700 break-words whitespace-normal">
          {goldenDisplay || formatTooltipValue(baseValue)}
        </span>
        <Chip size="sm" color="success" variant="flat" className="h-5 min-w-0">
          XLSX
        </Chip>
        <span className="pt-0.5 text-slate-700 break-words whitespace-normal">
          {formatTooltipValue(shownTrr)}
        </span>
        <Chip size="sm" color="danger" variant="flat" className="h-5 min-w-0">
          PDF
        </Chip>
        <span className="pt-0.5 text-slate-700 break-words whitespace-normal">
          {formatTooltipValue(shownPdf)}
        </span>
        {previousValue ? (
          <>
            <Chip
              size="sm"
              color="default"
              variant="flat"
              className="h-5 min-w-0"
            >
              Prev
            </Chip>
            <span className="pt-0.5 text-slate-700">
              {formatTooltipValue(previousValue)}
            </span>
          </>
        ) : null}
      </div>
      <p className="text-[11px] text-slate-500">
        Raw status: {control?.rawMatchStatus ?? "missing_both"}
      </p>
    </div>
  ) : null;

  const cellDiv = (
    <div
      className="group flex w-full cursor-pointer items-center justify-between gap-2 rounded-sm px-2 py-1"
      onMouseEnter={() => {
        if (!hasBeenHovered) setHasBeenHovered(true);
      }}
      onDoubleClick={() => {
        if (!isMissingPositionId) startEdit();
      }}
    >
      <div className="min-w-0 flex items-center gap-2">
        {!hideDot && <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dotClasses}`} />}
        <span
          className={`truncate ${!displayValue ? "text-gray-400" : ""}`}
          title={displayValue}
        >
          {displayValue}
        </span>
        {isMissingPositionId ? (
          <Chip
            size="sm"
            variant="flat"
            color="warning"
            className="h-5 text-[10px]"
          >
            missing position_id
          </Chip>
        ) : shownDelta ? (
          <Chip
            size="sm"
            variant="flat"
            color={shownDelta.color}
            className="h-5 text-[10px]"
          >
            {shownDelta.label}
          </Chip>
        ) : null}
      </div>
      <HeroTooltip
        content={!canEdit ? (editLockTooltip ?? "Locked") : "Edit value"}
        placement="top"
        delay={300}
        closeDelay={0}
        disableAnimation
      >
        <span className={canEdit ? "opacity-0 transition-opacity group-hover:opacity-100" : "opacity-0 transition-opacity group-hover:opacity-40"}>
          <button
            type="button"
            className={canEdit ? "text-gray-400 hover:text-gray-700" : "cursor-not-allowed text-gray-300"}
            onClick={() => {
              if (canEdit && !isMissingPositionId) startEdit();
            }}
            disabled={!canEdit || isMissingPositionId}
          >
            <PencilIcon className="h-3.5 w-3.5" />
          </button>
        </span>
      </HeroTooltip>
    </div>
  );

  return (
    <HeroTooltip
      disableAnimation
      content={tooltipContent}
      delay={0}
      closeDelay={0}
      placement="top-start"
    >
      {cellDiv}
    </HeroTooltip>
  );
});
